import React from 'react';
import { GridCellParams, GridEventListener, GridInputRowSelectionModel, GridRowOrderChangeParams, GridRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { CustomTable as Table } from '@zz2/zz2-ui';
import { getSelectedThemeLocalStorage } from '../../services/localStorageService';

export interface ICustomTableProps<T extends { [key : string] : any }> {
    id ?: string;
    rows : Array<T>;
    columns : Array<ICustomTableColumn>;
    enableQuickFilter ?: boolean;
    enableFiltering ?: boolean;
    enableSorting ?: boolean;
    enableAdding ?: boolean;
    enableEditing ?: ((row : T) => boolean | undefined) | boolean;
    enableDeleting ?: ((row : T) => boolean | undefined) | boolean;
    enableDetails ?: ((row : T) => boolean | undefined) | boolean;
    enableRefresh ?: boolean;
    enableSelection ?: boolean;
    enableCSVExport ?: boolean;
    enableRowReordering ?: boolean;
    csvExportPathname ?: string;
    enableClearFilterButton ?: boolean;
    disableRefreshButton ?: boolean;
    enableHorizontalVirtualize ?: boolean;
    enableDetailAccordion ?: ((row : T) => boolean | undefined) | boolean;
    detailAccordionHeight ?: number | 'auto';
    disableDeleteButton ?: ((row : T) => boolean | undefined) | boolean;
    disableDetailButton ?: ((row : T) => boolean | undefined) | boolean;
    disableEditButton ?: ((row : T) => boolean | undefined) | boolean;
    disableRowSelect ?: (row : T | GridValidRowModel) => boolean;
    disableAddButton ?: boolean;
    isActive ?: ((row : T) => boolean | undefined) | boolean;
    warning ?: ((row : T) => boolean | undefined) | boolean;
    rowColor ?: ((row : T) => string | undefined) | string;
    textColor ?: ((row : T) => string | undefined) | string;
    detailIcon ?: string | React.ReactElement<unknown>;
    detailsColor ?: string;
    deleteIcon ?: string | React.ReactElement<unknown>;
    deleteColor ?: string;
    editIcon ?: string | React.ReactElement<unknown>;
    addIcon ?: string | React.ReactElement<unknown>;
    editColor ?: string;
    addTooltipRightValidation ?: boolean;
    editTooltipRightValidation ?: boolean;
    deleteTooltipRightValidation ?: boolean;
    detailTooltip ?: ((row : T) => string | undefined) | string;
    deleteTooltip ?: ((row : T) => string | undefined) | string;
    editTooltip ?: ((row : T) => string | undefined) | string;
    addTooltip ?: string;
    addTooltipOnClick ?: () => void;
    editTooltipOnClick ?: () => void;
    deleteTooltipOnClick ?: () => void;
    addFunction ?: () => void;
    editFunction ?: (row : T) => void;
    deleteFunction ?: (row : T) => void;
    detailFunction ?: (row : T) => void;
    refreshFunction ?: () => void;
    onRowOrderChange ?: (params : GridRowOrderChangeParams) => void;
    detailAccordionComponent ?: (row : T) => React.ReactElement<unknown>;
    initialSortOrder ?: Array<{columnName : string; direction : 'asc' | 'desc'}>;
    enablePagination ?: boolean;
    enableTotalRow ?: boolean;
    pageSizes ?: Array<number>;
    fitWidthToPage ?: boolean;
    autoRowHeight ?: boolean;
    rowHeight ?: number;
    disableMultiSelect ?: boolean;
    hide ?: boolean;
    pageHeight ?: number;
    onSelectChange ?: (ids : GridRowSelectionModel) => void;
    selectedRows ?: GridInputRowSelectionModel;
    filterType ?: 'Textfield' | 'MultiCheckbox';
    filterTypeColumn ?: Array<{ column : string; data : Array<string> }>;
    isCellEditable ?: ((params : GridCellParams<T>) => boolean) | undefined;
    onCellEditStop ?: GridEventListener<'cellEditStop'>;
    filteredRows ?: (rows : Array<T>) => void;
    headerColor ?: 'light' | 'main';
    rowSelectTooltipMessage ?: ((rowId : number) => string) | string;
    useGuidAsParamsIdType ?: boolean;
    slimMode ?: boolean;
    hideFooter ?: boolean;
}

interface ICustomTableColumn {
    title : string;
    field : string;
    type ?: 'string' | 'number' | 'date' | 'boolean' | 'singleSelect' | 'actions' | string;
    width ?: number;
    flex ?: number;
    titleContainerComponent ?: (value : unknown) => React.ReactElement<unknown> | undefined;
    containerComponent ?: (row : unknown, value : unknown) => JSX.Element | React.ReactElement<unknown> | string | undefined;
    formatFunction ?: (value : any, row ?: any) => any;
    redFormatFunction ?: (value : any, row ?: any) => any;
    sortFunction ?: (value : any, value2 : any) => any;
    cellClassName ?: (row : unknown) => string;
    calculateTotal ?: boolean;
    calculateRedTotal ?: boolean;
    isNumberString ?: boolean;
    style ?: (value : unknown) => React.CSSProperties | undefined;
    freezeUpTo ?: boolean;
    enableFiltering ?: boolean;
    disableExport ?: boolean;
    enableSorting ?: boolean;
    dontFormatFilter ?: boolean;
    editable ?: boolean;
}

export const CustomTable = <T extends { [key : string] : any }>(props : ICustomTableProps<T>) => {
    const selectedTheme = getSelectedThemeLocalStorage();
    const { editColor, disableEditButton, ...rest } = props;

    let editButtonColor = 'cp';

    if (selectedTheme === 'Dark') {
        if (disableEditButton) {
            editButtonColor = '';
        } else {
            editButtonColor = 'cw';
        }
    } else {
        if (disableEditButton) {
            editButtonColor = '';
        } else {
            editButtonColor = 'cp';
        }
    }

    return <Table
        editColor={`${!editColor ? editButtonColor : editColor} p0`}
        disableEditButton={disableEditButton}
        {...rest}
    />;
};
